// Claim & Evidence — React port of kula-create's claim-evidence.html.
//
// Pedagogy: build an argument from mixed evidence. Strong/partial/distractor
// evidence is mixed; the learner must select N pieces + reason about why.
// After submit, the expert model is revealed with set-overlap visualisation.
//
// Single-row capture: { selectedEvidence: string[], reasoning: string }.
// answerKey is the expertModel.selectedEvidence array — set-equality scoring.

import { useState, useCallback, useRef } from 'react';
import { AnimatePresence } from 'framer-motion';
import type { ClaimEvidenceSlideData, Theme } from '../../slideTypes';
import { slideTypographyStyle } from '../../slideTypes';
import { feedbackStyle } from '../../shared/SlideShared';
import { Reveal } from '../../shared/Reveal';
import { ObsidianClaimEvidence } from './ObsidianClaimEvidence';
import type { ActivityResponsePayload } from '../../../../../types';

interface Props {
  data: ClaimEvidenceSlideData;
  theme: Theme;
  isEditing: boolean;
  onUpdate: (data: ClaimEvidenceSlideData) => void;
  onActivityResponse?: (response: ActivityResponsePayload) => void;
  onComplete?: () => void;
  savedResponse?: Record<string, unknown> | null;
}

export function ClaimEvidenceSlide({
  data,
  theme,
  isEditing,
  onUpdate,
  onActivityResponse,
  onComplete,
  savedResponse,
}: Props) {
  if (isEditing) return <ClaimEvidenceEditor data={data} theme={theme} onUpdate={onUpdate} />;
  // Theme-world vertical slice (THEME_WORLDS_PLAN.md flagship #2): the obsidian
  // theme gets a bespoke neo-noir renderer for claim-evidence. Branch above any
  // hooks so the default Student path's hooks are never conditionally skipped.
  if (theme.id === 'obsidian') {
    return <ObsidianClaimEvidence data={data} theme={theme} onActivityResponse={onActivityResponse} onComplete={onComplete} savedResponse={savedResponse} />;
  }
  return (
    <ClaimEvidenceStudent
      data={data}
      theme={theme}
      onActivityResponse={onActivityResponse}
      onComplete={onComplete}
      savedResponse={savedResponse}
    />
  );
}

function ClaimEvidenceStudent({
  data,
  theme,
  onActivityResponse,
  onComplete,
  savedResponse,
}: {
  data: ClaimEvidenceSlideData;
  theme: Theme;
  onActivityResponse?: (response: ActivityResponsePayload) => void;
  onComplete?: () => void;
  savedResponse?: Record<string, unknown> | null;
}) {
  const accent = theme.colors.accent || theme.colors.primary;

  // A saved {selectedEvidence, reasoning} payload describes the submitted end-state — restore the reveal view.
  const savedSelected = Array.isArray(savedResponse?.selectedEvidence)
    ? (savedResponse!.selectedEvidence as string[])
    : null;
  const restored = !!savedSelected;

  const [selected, setSelected] = useState<Set<string>>(() => new Set(savedSelected ?? []));
  const [reasoning, setReasoning] = useState(
    restored && typeof savedResponse?.reasoning === 'string' ? (savedResponse.reasoning as string) : '',
  );
  const [submitted, setSubmitted] = useState(restored);
  const [limitNotice, setLimitNotice] = useState(false);
  const completedRef = useRef(restored);

  // Shuffle the pool once per mount so authored order (strong → partial →
  // distractor in AI builds) can't leak evidence strength.
  const [shuffledPool] = useState(() => {
    const arr = [...data.evidencePool];
    for (let i = arr.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [arr[i], arr[j]] = [arr[j], arr[i]];
    }
    return arr;
  });

  // Sanitize authored rules at read time — NaN or inverted min/max must never brick the slide.
  const poolSize = data.evidencePool.length;
  const rawMin = Number(data.argumentRules.minEvidence);
  const rawMax = Number(data.argumentRules.maxEvidence);
  let minEvidence = Number.isFinite(rawMin) ? Math.max(1, Math.round(rawMin)) : 1;
  let maxEvidence = Number.isFinite(rawMax)
    ? Math.max(1, Math.round(rawMax))
    : Math.max(1, poolSize);
  if (poolSize > 0) {
    minEvidence = Math.min(minEvidence, poolSize);
    maxEvidence = Math.min(maxEvidence, poolSize);
  }
  if (minEvidence > maxEvidence) [minEvidence, maxEvidence] = [maxEvidence, minEvidence];
  const { requireReasoning } = data.argumentRules;

  const count = selected.size;
  const ready =
    count >= minEvidence &&
    count <= maxEvidence &&
    (!requireReasoning || reasoning.trim().length > 0);

  const toggle = (id: string) => {
    if (submitted) return;
    if (!selected.has(id) && selected.size >= maxEvidence) {
      setLimitNotice(true);
      window.setTimeout(() => setLimitNotice(false), 2500);
      return;
    }
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const submit = useCallback(() => {
    setSubmitted(true);
    // Local grading — set-equality vs the expert picks (mirrors RevealPredictSlide's isCorrect/score keys).
    const expert = new Set(data.expertModel.selectedEvidence);
    const intersection = [...selected].filter((id) => expert.has(id)).length;
    const isCorrect = selected.size === expert.size && intersection === expert.size;
    const score = expert.size > 0 ? Math.round((intersection / expert.size) * 100) : 0;
    onActivityResponse?.({
      activityType: 'claim-evidence',
      mode: 'quiz',
      responseData: { selectedEvidence: Array.from(selected), reasoning },
      isCorrect,
      score,
    });
    if (!completedRef.current) {
      completedRef.current = true;
      onComplete?.();
    }
  }, [data.expertModel.selectedEvidence, selected, reasoning, onActivityResponse, onComplete]);

  const expertSet = new Set(data.expertModel.selectedEvidence);
  const overlapCount = [...selected].filter((id) => expertSet.has(id)).length;

  return (
    <div
      className="h-full w-full overflow-y-auto"
      style={{ background: theme.colors.background, color: theme.colors.text, fontFamily: theme.fonts.body }}
    >
      <div className="mx-auto max-w-[760px] px-6 py-10">
        <header className="text-center mb-6">
          <h1 style={{ ...slideTypographyStyle(theme, 'heading'), color: theme.colors.text, marginBottom: 8 }}>
            {data.title}
          </h1>
          {data.subtitle && <p style={{ color: theme.colors.textMuted, fontSize: '0.95rem' }}>{data.subtitle}</p>}
        </header>

        {/* Claim card */}
        <div
          className="rounded-lg p-6 mb-6 border-l-4"
          style={{ background: theme.colors.surface, borderColor: accent }}
        >
          <span className="inline-block text-xs font-bold tracking-wider uppercase mb-2" style={{ color: accent }}>
            The Claim
          </span>
          <p className="text-lg font-semibold mb-2" style={{ color: theme.colors.text }}>
            {data.claim.statement}
          </p>
          {data.claim.context && (
            <p className="text-sm" style={{ color: theme.colors.textMuted }}>
              {data.claim.context}
            </p>
          )}
        </div>

        {/* Evidence pool */}
        <div className="mb-6">
          <h3 className="text-xs font-bold tracking-wider uppercase mb-3" style={{ color: theme.colors.textMuted }}>
            Evidence Pool
          </h3>
          <div className="grid sm:grid-cols-2 gap-3">
            {shuffledPool.map((ev) => {
              const isSelected = selected.has(ev.id);
              const showStrength = submitted;
              const strengthColor = feedbackStyle(
                theme,
                ev.strength === 'strong' ? 'correct' : ev.strength === 'partial' ? 'warning' : 'incorrect',
              ).text;
              return (
                <button
                  key={ev.id}
                  onClick={() => toggle(ev.id)}
                  disabled={submitted}
                  className="text-left rounded-lg p-4 border-2 transition-all"
                  style={{
                    background: isSelected ? `${accent}10` : theme.colors.surface,
                    borderColor: isSelected ? accent : `${theme.colors.textMuted}33`,
                    cursor: submitted ? 'default' : 'pointer',
                    opacity: submitted && !isSelected ? 0.6 : 1,
                  }}
                >
                  <p className="font-semibold text-sm mb-1" style={{ color: theme.colors.text }}>
                    {ev.label}
                  </p>
                  <p className="text-xs leading-relaxed" style={{ color: theme.colors.textMuted }}>
                    {ev.detail}
                  </p>
                  {showStrength && (
                    <p
                      className="text-xs font-bold uppercase tracking-wider mt-2"
                      style={{ color: strengthColor }}
                    >
                      {ev.strength}
                    </p>
                  )}
                </button>
              );
            })}
          </div>
        </div>

        {/* Argument zone */}
        {!submitted && (
          <div className="mb-4">
            <p className="text-sm font-medium mb-2" style={{ color: theme.colors.text }}>
              Your selection: {count} of {minEvidence}–{maxEvidence}
            </p>
            {limitNotice && (
              <p className="text-xs font-semibold mb-2" style={{ color: '#dc2626' }}>
                Max {maxEvidence} — deselect one first.
              </p>
            )}
            {data.argumentRules.requireReasoning && (
              <textarea
                value={reasoning}
                onChange={(e) => setReasoning(e.target.value)}
                placeholder="Why does this evidence support the claim?"
                maxLength={1000}
                rows={4}
                className="w-full px-3 py-2 rounded border text-sm"
                style={{
                  background: theme.colors.surface,
                  borderColor: `${theme.colors.textMuted}33`,
                  color: theme.colors.text,
                }}
              />
            )}
            <button
              onClick={submit}
              disabled={!ready}
              className="mt-3 w-full px-6 py-3 rounded-lg font-semibold text-sm"
              style={{
                background: accent,
                color: '#fff',
                cursor: ready ? 'pointer' : 'not-allowed',
                opacity: ready ? 1 : 0.4,
              }}
            >
              Submit argument
            </button>
          </div>
        )}

        {/* Expert model reveal */}
        <AnimatePresence>
        {submitted && (
          <Reveal variant="up" className="space-y-4 mt-6">
            {/* Payoff register: heavier accent rule + larger radius than the setup card */}
            <div
              className="rounded-xl border-l-[5px] p-5 shadow-sm"
              style={{ background: `${accent}15`, borderColor: accent }}
            >
              <p className="text-sm font-semibold mb-2" style={{ color: accent }}>
                You matched {overlapCount} of {data.expertModel.selectedEvidence.length} expert picks.
              </p>
              <p className="text-xs uppercase tracking-wider font-bold mb-2" style={{ color: accent }}>
                Expert Analysis
              </p>
              <ul className="space-y-1 mb-3 text-sm" style={{ color: theme.colors.text }}>
                {data.expertModel.selectedEvidence.map((id) => {
                  const ev = data.evidencePool.find((e) => e.id === id);
                  return ev ? (
                    <li key={id} className="ml-4 list-disc">
                      <strong>{ev.label}</strong>
                      {ev.expertReasoning && <> — {ev.expertReasoning}</>}
                    </li>
                  ) : null;
                })}
              </ul>
              {data.expertModel.reasoning && (
                <p className="text-sm leading-relaxed" style={{ color: theme.colors.text }}>
                  {data.expertModel.reasoning}
                </p>
              )}
            </div>

            {data.counterClaim && (
              <div
                className="rounded-lg p-5"
                style={{ background: theme.colors.surface, border: `1px solid ${theme.colors.textMuted}33` }}
              >
                <p className="text-xs uppercase tracking-wider font-bold mb-2" style={{ color: theme.colors.textMuted }}>
                  Counter-Claim
                </p>
                <p className="font-semibold text-sm mb-2" style={{ color: theme.colors.text }}>
                  {data.counterClaim.statement}
                </p>
                <p className="text-xs" style={{ color: theme.colors.textMuted }}>
                  Supported by: {data.counterClaim.strongEvidence.map((id) => data.evidencePool.find((e) => e.id === id)?.label).filter(Boolean).join(', ')}
                </p>
              </div>
            )}

            {data.reflection?.prompt && (
              <div
                className="rounded-lg p-5 text-center"
                style={{ background: theme.colors.surface, border: `2px solid ${accent}` }}
              >
                <p className="text-xs uppercase tracking-wider font-bold mb-2" style={{ color: accent }}>
                  Reflect
                </p>
                <p className="text-sm" style={{ color: theme.colors.textMuted }}>
                  {data.reflection.prompt}
                </p>
              </div>
            )}
          </Reveal>
        )}
        </AnimatePresence>
      </div>
    </div>
  );
}

// ---- Editor (minimal — JSON-style fields) ---------------------------------

function ClaimEvidenceEditor({
  data,
  theme,
  onUpdate,
}: {
  data: ClaimEvidenceSlideData;
  theme: Theme;
  onUpdate: (data: ClaimEvidenceSlideData) => void;
}) {
  const accent = theme.colors.accent || theme.colors.primary;
  const inputStyle = {
    background: theme.colors.background,
    borderColor: `${theme.colors.textMuted}33`,
    color: theme.colors.text,
  } as const;

  return (
    <div className="h-full w-full overflow-y-auto p-8">
      <div className="mx-auto max-w-[760px] space-y-4">
        <input
          value={data.title}
          onChange={(e) => onUpdate({ ...data, title: e.target.value })}
          placeholder="Title…"
          className="w-full bg-transparent border-b-2 border-transparent hover:border-gray-300 focus:border-blue-500 focus:outline-none"
          style={{ ...slideTypographyStyle(theme, 'heading'), color: theme.colors.text }}
        />
        <input
          value={data.subtitle}
          onChange={(e) => onUpdate({ ...data, subtitle: e.target.value })}
          placeholder="Subtitle"
          className="w-full bg-transparent border-b border-transparent hover:border-gray-300 focus:border-blue-500 focus:outline-none text-sm"
          style={{ color: theme.colors.textMuted }}
        />

        <div className="rounded-lg border p-4" style={{ background: theme.colors.surface, borderColor: `${theme.colors.textMuted}33` }}>
          <label className="text-xs font-bold uppercase tracking-wider" style={{ color: accent }}>Claim</label>
          <textarea
            value={data.claim.statement}
            onChange={(e) => onUpdate({ ...data, claim: { ...data.claim, statement: e.target.value } })}
            placeholder="Claim statement"
            rows={2}
            className="w-full mt-2 px-3 py-2 rounded border text-sm"
            style={inputStyle}
          />
          <input
            value={data.claim.context ?? ''}
            onChange={(e) => onUpdate({ ...data, claim: { ...data.claim, context: e.target.value } })}
            placeholder="Optional context"
            className="w-full mt-2 px-3 py-2 rounded border text-sm"
            style={inputStyle}
          />
        </div>

        <div className="rounded-lg border p-4" style={{ background: theme.colors.surface, borderColor: `${theme.colors.textMuted}33` }}>
          <div className="flex items-center justify-between mb-2">
            <label className="text-xs font-bold uppercase tracking-wider" style={{ color: accent }}>Evidence pool</label>
            <button
              onClick={() => onUpdate({ ...data, evidencePool: [...data.evidencePool, { id: crypto.randomUUID(), label: '', detail: '', strength: 'strong' }] })}
              className="text-xs"
              style={{ color: accent }}
            >
              + Add evidence
            </button>
          </div>
          {data.evidencePool.map((ev) => (
            <div key={ev.id} className="rounded p-3 mb-2" style={{ background: theme.colors.background, border: `1px solid ${theme.colors.textMuted}22` }}>
              <div className="flex gap-2 mb-2">
                <input
                  value={ev.label}
                  onChange={(e) =>
                    onUpdate({ ...data, evidencePool: data.evidencePool.map((x) => (x.id === ev.id ? { ...x, label: e.target.value } : x)) })
                  }
                  placeholder="Short label (3-6 words)"
                  className="flex-1 px-2 py-1 rounded border text-sm"
                  style={inputStyle}
                />
                <select
                  value={ev.strength}
                  onChange={(e) =>
                    onUpdate({
                      ...data,
                      evidencePool: data.evidencePool.map((x) =>
                        x.id === ev.id ? { ...x, strength: e.target.value as 'strong' | 'partial' | 'distractor' } : x,
                      ),
                    })
                  }
                  className="text-xs px-2 py-1 rounded border"
                  style={inputStyle}
                >
                  <option value="strong">Strong</option>
                  <option value="partial">Partial</option>
                  <option value="distractor">Distractor</option>
                </select>
                <button
                  onClick={() => onUpdate({ ...data, evidencePool: data.evidencePool.filter((x) => x.id !== ev.id) })}
                  className="text-xs text-gray-400 hover:text-red-500"
                >
                  ×
                </button>
              </div>
              <input
                value={ev.detail}
                onChange={(e) => onUpdate({ ...data, evidencePool: data.evidencePool.map((x) => (x.id === ev.id ? { ...x, detail: e.target.value } : x)) })}
                placeholder="Detail (max 120 chars)"
                maxLength={120}
                className="w-full mb-2 px-2 py-1 rounded border text-sm"
                style={inputStyle}
              />
              <input
                value={ev.expertReasoning ?? ''}
                onChange={(e) =>
                  onUpdate({
                    ...data,
                    evidencePool: data.evidencePool.map((x) => (x.id === ev.id ? { ...x, expertReasoning: e.target.value } : x)),
                  })
                }
                placeholder="Expert reasoning (shown after submit)"
                className="w-full px-2 py-1 rounded border text-xs"
                style={inputStyle}
              />
            </div>
          ))}
        </div>

        <div className="rounded-lg border p-4" style={{ background: theme.colors.surface, borderColor: `${theme.colors.textMuted}33` }}>
          <label className="text-xs font-bold uppercase tracking-wider" style={{ color: accent }}>Argument rules</label>
          <div className="grid grid-cols-3 gap-2 mt-2 text-sm">
            <label>
              Min
              <input
                type="number"
                min={1}
                value={data.argumentRules.minEvidence}
                onChange={(e) => {
                  // Clamp to sane integers — NaN (cleared field) falls back to 1; max follows min upward.
                  const poolSize = Math.max(1, data.evidencePool.length);
                  const raw = Math.round(Number(e.target.value));
                  const min = Math.min(Number.isFinite(raw) ? Math.max(1, raw) : 1, poolSize);
                  const currentMax = Number.isFinite(data.argumentRules.maxEvidence)
                    ? data.argumentRules.maxEvidence
                    : poolSize;
                  onUpdate({
                    ...data,
                    argumentRules: { ...data.argumentRules, minEvidence: min, maxEvidence: Math.max(min, currentMax) },
                  });
                }}
                className="w-full px-2 py-1 rounded border"
                style={inputStyle}
              />
            </label>
            <label>
              Max
              <input
                type="number"
                min={1}
                value={data.argumentRules.maxEvidence}
                onChange={(e) => {
                  // Clamp: min ≥ 1, max ≥ min, max ≤ pool size; NaN falls back to pool size.
                  const poolSize = Math.max(1, data.evidencePool.length);
                  const raw = Math.round(Number(e.target.value));
                  const currentMin = Number.isFinite(data.argumentRules.minEvidence)
                    ? Math.max(1, data.argumentRules.minEvidence)
                    : 1;
                  const max = Math.min(Number.isFinite(raw) ? Math.max(currentMin, raw) : poolSize, poolSize);
                  onUpdate({
                    ...data,
                    argumentRules: { ...data.argumentRules, maxEvidence: max, minEvidence: Math.min(currentMin, max) },
                  });
                }}
                className="w-full px-2 py-1 rounded border"
                style={inputStyle}
              />
            </label>
            <label className="flex items-center gap-2 mt-5">
              <input
                type="checkbox"
                checked={data.argumentRules.requireReasoning}
                onChange={(e) =>
                  onUpdate({
                    ...data,
                    argumentRules: { ...data.argumentRules, requireReasoning: e.target.checked },
                  })
                }
              />
              <span className="text-xs">Require reasoning</span>
            </label>
          </div>
        </div>

        <div className="rounded-lg border p-4" style={{ background: theme.colors.surface, borderColor: `${theme.colors.textMuted}33` }}>
          <label className="text-xs font-bold uppercase tracking-wider" style={{ color: accent }}>Expert model (correct picks)</label>
          <p className="text-xs mb-2" style={{ color: theme.colors.textMuted }}>
            Toggle which evidence the expert picks. Set-equality scoring against this list.
          </p>
          <div className="space-y-1">
            {data.evidencePool.map((ev) => {
              const checked = data.expertModel.selectedEvidence.includes(ev.id);
              return (
                <label key={ev.id} className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={(e) =>
                      onUpdate({
                        ...data,
                        expertModel: {
                          ...data.expertModel,
                          selectedEvidence: e.target.checked
                            ? [...data.expertModel.selectedEvidence, ev.id]
                            : data.expertModel.selectedEvidence.filter((id) => id !== ev.id),
                        },
                      })
                    }
                  />
                  {ev.label || '(unnamed)'}
                </label>
              );
            })}
          </div>
          <textarea
            value={data.expertModel.reasoning}
            onChange={(e) =>
              onUpdate({ ...data, expertModel: { ...data.expertModel, reasoning: e.target.value } })
            }
            placeholder="Expert reasoning"
            rows={3}
            className="w-full mt-2 px-3 py-2 rounded border text-sm"
            style={inputStyle}
          />
        </div>

        <div className="rounded-lg border p-4" style={{ background: theme.colors.surface, borderColor: `${theme.colors.textMuted}33` }}>
          <label className="text-xs font-bold uppercase tracking-wider" style={{ color: accent }}>Reflection prompt</label>
          <textarea
            value={data.reflection?.prompt ?? ''}
            onChange={(e) => onUpdate({ ...data, reflection: { prompt: e.target.value } })}
            rows={2}
            className="w-full mt-2 px-3 py-2 rounded border text-sm"
            style={inputStyle}
          />
        </div>
      </div>
    </div>
  );
}
